import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { OrderDetailModel } from 'src/app/models/preliminaries/order-detail.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { CostCenterPriceService } from 'src/app/services/archive/cost-center-price.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

import { EventEmitter, Input, Output } from '@angular/core';
import { OrderTypeEnums } from 'src/app/enum/order-enums';
import { CostCenterPriceModel } from 'src/app/models/archive/cost-center-price.model';
import { OrderModel } from 'src/app/models/preliminaries/order.model';

@Component({
  selector: 'app-register-order-adjustment-detail',
  templateUrl: './register-order-adjustment-detail.component.html',
  styleUrls: ['./register-order-adjustment-detail.component.scss'],
  standalone: false
})
export class RegisterOrderAdjustmentDetailComponent implements OnInit, OnChanges {

  @Input() costCenterPriceContract: CostCenterPriceModel = new CostCenterPriceModel();
  @Input() orderContract: OrderModel = new OrderModel();
  @Input() orderDetailContract: OrderDetailModel = new OrderDetailModel();
  @Input() orderAdjustmentDetails: OrderDetailModel[] = [];
  @Output() costCenterPriceRegister = new EventEmitter<CostCenterPriceModel>();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() openOrderPriceModal = new EventEmitter<number>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  codeNameArticle: string = '';
  disableButtonSave: boolean = false;
  formOrderDetail: FormGroup = new FormGroup({});
  orderDetailId: number = 0;
  rentalInvoicingConceptId: number = 0
  swGeneric: boolean = false;

  constructor(private articleService: ArticleService,
    private conceptInvoicingService: ConceptInvoicingService,
    private costCenterPriceService: CostCenterPriceService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private orderDetailService: OrderDetailService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new OrderDetailModel());
  }

  async ngOnInit() {
    await this.getConceptInvoicingRental();
  }

  async ngOnChanges(changes: SimpleChanges) {
    const { costCenterPriceContract, orderAdjustmentDetails, orderDetailContract } = changes;
    if (costCenterPriceContract && !costCenterPriceContract.firstChange) {
      return await this.getCostCenterPriceRegister(costCenterPriceContract.currentValue);
    }
    if (orderAdjustmentDetails && !orderAdjustmentDetails.firstChange) {
      return await this.createOrderDetails(this.orderAdjustmentDetails);
    }
    if (orderDetailContract && !orderDetailContract.firstChange) {
      if (orderDetailContract.currentValue.orderDetailId === 0) {
        this.orderDetailId = 0;
        this.validateForm(orderDetailContract.currentValue);
      } else {
        this.orderDetailId = orderDetailContract.currentValue.orderDetailId;
        this.validateForm(orderDetailContract.currentValue);
      }
    }
  }

  changeQuantityFinal(): boolean {
    const quantityFinal = this.formOrderDetail.get("quantityFinal")?.value;
    if (quantityFinal < this.orderDetailContract.quantityRemission) {
      this.toastHandlerService.showToast('warning', 'La cantidad Final no puede ser menor a la cantidad remisión', 'Atención');
      return false;
    }
    return true;
  }


  async clearForm() {
    this.orderDetailId = 0;
    this.codeNameArticle = '';
    this.isFormView.emit(false);
    this.validateForm(new OrderDetailModel())
  }

  createCostCenterPrice() {
    const costCenterPriceRegister = new CostCenterPriceModel();
    costCenterPriceRegister.costCenterId = this.orderContract.costCenterId;
    costCenterPriceRegister.articleId = this.orderDetailContract.articleId;
    this.costCenterPriceRegister.emit(costCenterPriceRegister);
  }

  async createAndUpdateOrderDetail(orderDetail: OrderDetailModel) {
    if (this.orderDetailId === 0) {
      await this.createOrderDetail(orderDetail);
    } else {
      await this.updateOrderDetail(orderDetail);
    }
  }

  async createOrderDetails(orderDetails: OrderDetailModel[]) {
    try {
      await firstValueFrom(this.orderDetailService.createOrderDetails(orderDetails, this.orderContract.orderId));
      this.toastHandlerService.showToast('success', 'Los detalles del pedido fueron creados exitosamente');
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear los detalles del pedido.', error);
    }
  }

  async createOrderDetail(orderDetail: OrderDetailModel) {
    try {
      orderDetail.isOrderAdjustment = true;
      await firstValueFrom(this.orderDetailService.createOrderDetail(orderDetail));
      this.toastHandlerService.showToast('success', 'El detalle del pedido fue creado exitosamente.');
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el detalle del pedido.', error);
    }
  }

  async getArticle(articleId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.codeNameArticle = `${article.code} - ${article.name}`;
      this.swGeneric = article.swGeneric;
    } catch (error) {
      this.swGeneric = false;
      this.codeNameArticle = '';
      console.error('Error al obtener el artículo por Id:', error);
    }
  }

  async getConceptInvoicingRental() {
    try {
      const response = await firstValueFrom(this.conceptInvoicingService.getConceptInvoicingAlquiler());
      this.rentalInvoicingConceptId = response.conceptInvoicingId;
    } catch (error) {
      console.error('Error al obtener el concepto de facturación para alquiler.', error);
    }
  }

  async getCostCenterPriceRegister(costCenterPriceContract: CostCenterPriceModel) {
    if (costCenterPriceContract.costCenterPriceId) {
      this.orderDetailId = this.orderDetailContract.orderDetailId;
      this.orderDetailContract.costCenterPrice = costCenterPriceContract;
      await this.createAndUpdateOrderDetail(this.orderDetailContract);
    }
  }

  async getCostCenterPriceByKeys(costCenterId: number, articleId: number) {
    try {
      if (!costCenterId || !articleId) {
        this.disableButtonSave = true;
        return;
      }
      const response = await firstValueFrom(this.costCenterPriceService.getCostCenterPriceByKeys(costCenterId,
        this.rentalInvoicingConceptId, articleId));
      if (response.costCenterPriceId != 0 && response.baseUnitValue != -1) {
        //Existe en precios por centro de costo con precio válido
        this.disableButtonSave = false;
        this.costCenterPriceContract = response;
      } else if (response.costCenterPriceId == 0 && response.baseUnitValue != -1) {
        //NO existe en precios CCO pero SÍ en lista de precios
        this.disableButtonSave = false;
        this.costCenterPriceContract = response;
      } else {
        //Existe en CCO pero sin precio base válido
        this.disableButtonSave = true;
        this.toastHandlerService.showToast('error', 'El artículo NO se encuentra registrado en la lista de Precios.');
      }
    } catch (error) {
      this.disableButtonSave = true;
      console.error('Error el precio por centro de costos.', error);
      throw error;
    }
  }

  async getStockArticle(articleId: number) {
    try {
      const response = await firstValueFrom(this.orderDetailService.getStocksByArticleIdByOrder(this.orderContract, articleId));
      setTimeout(() => {
        this.formOrderDetail.patchValue({
          quantityShop: response.valueDecimalItem,
          quantityWorkshop: response.valueLongItem
        });
      })
    } catch (error) {
      console.error('Error al obtener el stock del artículo.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formOrderDetail.valid) {
      return this.showValidationMessages();
    }
    const modelOrderDetail = this.formOrderDetail.getRawValue() as OrderDetailModel;
    if (this.changeQuantityFinal()) {
      if (this.costCenterPriceContract.costCenterPriceId == 0) {
        if (this.swGeneric) {
          if (this.orderDetailId == 0) {
            await this.createOrderDetail(modelOrderDetail);
            this.openOrderPriceModal.emit(this.orderDetailId);
          } else {
            this.orderDetailContract.quantityFinal = modelOrderDetail.quantityFinal;
            await this.updateOrderDetail(this.orderDetailContract);
          }
        } else {
          this.orderDetailContract.orderDetailId = this.orderDetailId;
          this.orderDetailContract.articleId = modelOrderDetail.articleId;
          this.orderDetailContract.quantityFinal = modelOrderDetail.quantityFinal;
          this.orderDetailContract.costCenterPrice = this.costCenterPriceContract;
          if (this.orderContract.orderTypeId != OrderTypeEnums.rental) {
            await this.createAndUpdateOrderDetail(this.orderDetailContract);
          } else {
            this.createCostCenterPrice();
          }
        }
      } else {
        modelOrderDetail.costCenterPrice = this.costCenterPriceContract;
        if (this.orderDetailId == 0) {
          await this.createOrderDetail(modelOrderDetail);
        } else {
          this.orderDetailContract.quantityFinal = modelOrderDetail.quantityFinal;
          this.orderDetailContract.costCenterPrice = this.costCenterPriceContract;
          await this.updateOrderDetail(this.orderDetailContract);
        }
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      orderId: {
        required: 'El campo pedido es obligatorio.',
        min: 'Debes seleccionar un pedido válido.',
      },
      articleId: {
        required: 'El campo artículo es obligatorio.',
        min: 'Debes seleccionar un artículo válido.',
      },
      quantityFinal: {
        min: 'La Cant.Fin. mínima permitida es 0.',
        max: 'La Cant.Fin. no puede superar 9.999.999,99.',
      },
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formOrderDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateOrderDetail(orderDetail: OrderDetailModel) {
    try {
      await firstValueFrom(this.orderDetailService.updateOrderDetail(orderDetail));
      this.toastHandlerService.showToast('success', 'El detalle del pedido fue modificado exitosamente.');
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el detalle del pedido.', error);
    }
  }

  async validateForm(orderDetail: OrderDetailModel) {
    this.formOrderDetail = this.formBuilder.group({
      shopStock: [orderDetail.shopStock],
      workshopStock: [orderDetail.workshopStock],
      rentalUnitId: [orderDetail.rentalUnitId, [Validators.required, Validators.min(1)]],
      orderId: [orderDetail.orderId, [Validators.required, Validators.min(1)]],
      articleId: [orderDetail.articleId, [Validators.required, Validators.min(1)]],
      quantityFinal: [orderDetail.quantityFinal, [Validators.min(0), Validators.max(9999999.99)]],
    });
    setTimeout(() => this.formControlStateService.setControlState(this.formOrderDetail, ['shopStock', 'workshopStock'], false));
    const articleId = orderDetail.articleId;
    if (articleId > 0) {
      this.isFormView.emit(true);
      await Promise.all([
        this.getArticle(articleId),
        this.getStockArticle(articleId),
        this.getCostCenterPriceByKeys(this.orderContract.costCenterId, articleId)
      ]);
    }
  }

  validateFormControls() {
    Object.keys(this.formOrderDetail.controls).forEach((key) => {
      const control = this.formOrderDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
